import React, { useState, useEffect, useRef, useCallback } from "react";
import { get } from "src/utils/axios";
import { Link } from "react-router-dom/cjs/react-router-dom";
import {
  SpeakerphoneIcon,
  NewspaperIcon,
  BookOpenIcon,
  ClipboardIcon,
  UserIcon,
  PhoneIcon,
  XIcon,
} from "@heroicons/react/outline";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { currentOfficeSelector, officesSelector } from "src/slices/office";
import {
  globalSearchTermSelector,
  globalSearchOfficeSelector,
  setGlobalSearchTerm,
  setGlobalSearchOffice,
  clearGlobalSearch
} from "src/slices/global";
import Select from "src/components/basic/select";

const GlobalSearch = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const currentOffice = useSelector(currentOfficeSelector);
  const userOffices = useSelector(officesSelector);
  const persistedSearchTerm = useSelector(globalSearchTermSelector);
  const persistedOfficeId = useSelector(globalSearchOfficeSelector);

  const [searchTerm, setSearchTerm] = useState(persistedSearchTerm);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // State to control dropdown visibility
  const [isFocused, setIsFocused] = useState(false); // Track if input is focused
  const dropdownRef = useRef(null); // Ref to the dropdown element
  const inputRef = useRef(null); // Ref to the input element
  const [selectedOfficeId, setSelectedOfficeId] = useState(persistedOfficeId); // null means "All Offices"
  const [publicOffices, setPublicOffices] = useState([]);
  const [allOffices, setAllOffices] = useState([]);

  // Sync local state with Redux when persisted values change
  useEffect(() => {
    setSearchTerm(persistedSearchTerm);
    setSelectedOfficeId(persistedOfficeId);
  }, [persistedSearchTerm, persistedOfficeId]);

  // Fetch public offices on mount
  useEffect(() => {
    const fetchPublicOffices = async () => {
      try {
        const response = await get("api/v1/public_offices");
        setPublicOffices(response.data.data || []);
      } catch (error) {
        console.error("Error fetching public offices:", error);
      }
    };
    fetchPublicOffices();
  }, []);

  // Combine user offices and public offices
  useEffect(() => {
    const combined = [...(userOffices || []), ...(publicOffices || [])];
    // Remove duplicates by id
    const uniqueOffices = combined.reduce((acc, office) => {
      if (!acc.find(o => o.id === office.id)) {
        acc.push(office);
      }
      return acc;
    }, []);
    setAllOffices(uniqueOffices);
  }, [userOffices, publicOffices]);

  // Function to fetch search results
  const fetchSearchResults = useCallback(async (term, officeId, signal) => {
    if (!currentOffice?.id) return;

    try {
      setIsLoading(true);

      // Build query string - only include office_id if one is selected
      let queryString = `api/v1/search?query=${encodeURIComponent(term)}`;
      if (officeId) {
        queryString += `&office_id=${officeId}`;
      }

      const response = await get(queryString, { signal });
      const data = response.data.data;

      // Combine all results into a single array with type information
      const combinedResults = [
        ...(data.posts || []).map(item => ({ ...item, type: 'post' })),
        ...(data.bills || []).map(item => ({ ...item, type: 'bill' })),
        ...(data.topics || []).map(item => ({ ...item, type: 'topic' })),
        ...(data.issues || []).map(item => ({ ...item, type: 'issue' })),
        ...(data.people || []).map(item => ({ ...item, type: 'person' })),
        ...(data.contacts || []).map(item => ({ ...item, type: 'contact' })),
      ];

      setSearchResults(combinedResults.slice(0, 10));
      // Only open dropdown if input is focused
      if (combinedResults.length > 0 && isFocused) {
        setIsDropdownOpen(true);
      }
    } catch (error) {
      // Ignore abort errors
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error("Error fetching search results:", error);
      }
    } finally {
      // Only set loading to false if request wasn't aborted
      if (!signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [currentOffice, isFocused]);

  // Effect to call fetchSearchResults after user stops typing or office filter changes
  useEffect(() => {
    // Cancel any pending request immediately
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (searchTerm.length >= 3) {
      // Set a new timer
      debounceTimerRef.current = setTimeout(() => {
        // Create new AbortController for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        fetchSearchResults(searchTerm, selectedOfficeId, abortController.signal);
      }, 500); // 500ms debounce for better performance
    } else {
      setSearchResults([]);
      setIsDropdownOpen(false);
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [searchTerm, selectedOfficeId, fetchSearchResults]);

  // Function to close the dropdown when clicking outside
  const closeDropdown = e => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)) {
      setIsDropdownOpen(false);
      setIsFocused(false);
    }
  };

  // Add event listener to close dropdown on outside click
  useEffect(() => {
    document.addEventListener("click", closeDropdown);
    return () => document.removeEventListener("click", closeDropdown);
  }, []);

  const clearOnClick = e => {
    e.stopPropagation();
    setIsDropdownOpen(false);
    setIsFocused(false);
    setSearchTerm("");
    setSearchResults([]);
    dispatch(clearGlobalSearch());
  };

  const handleResultClick = () => {
    setIsDropdownOpen(false);
    setIsFocused(false);
  };

  const handleKeyDown = e => {
    if (e.key === "Enter" && searchTerm.trim().length > 0) {
      e.preventDefault();
      setIsDropdownOpen(false);
      const url = selectedOfficeId
        ? `/search?term=${encodeURIComponent(searchTerm)}&office_id=${selectedOfficeId}`
        : `/search?term=${encodeURIComponent(searchTerm)}`;
      history.push(url);
    }
  };

  // Get icon and link based on result type
  const getResultConfig = (result) => {
    const { type, id, attributes } = result;

    const configs = {
      post: {
        icon: <SpeakerphoneIcon />,
        link: `/posts/${id}/detail?oid=${attributes.office_id}`,
        title: attributes.title,
        subtitle: attributes.description?.substring(0, 50).replace(/(<([^>]+)>)/gi, ""),
        label: "Post"
      },
      bill: {
        icon: <NewspaperIcon />,
        link: `/data/bills/${id}`,
        title: attributes.name || attributes.bill_number,
        subtitle: attributes.bill_number,
        label: "Bill"
      },
      topic: {
        icon: <BookOpenIcon />,
        link: `/data/topics/${id}?office_id=${attributes.office_id}`,
        title: attributes.name,
        subtitle: null,
        label: "Topic"
      },
      issue: {
        icon: <ClipboardIcon />,
        link: `/data/issues/${id}?office_id=${attributes.office_id}`,
        title: attributes.name,
        subtitle: null,
        label: "Subtopic"
      },
      person: {
        icon: <UserIcon />,
        link: `/people/${id}`,
        title: attributes.full_name || `${attributes.first_name} ${attributes.last_name}`,
        subtitle: attributes.current_role,
        label: "Person"
      },
      contact: {
        icon: <PhoneIcon />,
        link: `/contacts/${id}?office_id=${attributes.office_id}`,
        title: attributes.name,
        subtitle: attributes.email,
        label: "Contact"
      }
    };

    return configs[type] || configs.post;
  };

  return (
    <div className="relative w-full">
      <div className="relative flex w-full">
        <div className="relative flex-1 flex min-w-0">
          {/* Office filter embedded inside search input */}
          <div className="relative flex-shrink-0 border border-gray-300 border-r-0 rounded-l-sm overflow-hidden w-20 sm:w-24 md:w-32">
            <style>{`
              .custom-select-no-arrow {
                -webkit-appearance: none !important;
                -moz-appearance: none !important;
                appearance: none !important;
                background-image: none !important;
              }
              .custom-select-no-arrow::-ms-expand {
                display: none;
              }
            `}</style>
            <select
              className="custom-select-no-arrow h-10 w-full px-1 sm:px-2 text-xs border-none focus:outline-none focus:ring-0 bg-white cursor-pointer pr-4 sm:pr-6"
              value={selectedOfficeId || ""}
              onChange={(e) => {
                const value = e.target.value === "" ? null : parseInt(e.target.value);
                setSelectedOfficeId(value);
                dispatch(setGlobalSearchOffice(value));
              }}
            >
              <option value="">All</option>
              {allOffices.map(office => (
                <option key={office.id} value={office.id}>
                  {office.attributes?.name || "Unknown Office"}
                </option>
              ))}
            </select>
            <div className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <input
            ref={inputRef}
            className="border border-gray-300 h-10 rounded-r-sm py-1 px-2 sm:px-3 md:px-4 flex-1 text-xs sm:text-sm md:text-base min-w-0"
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              dispatch(setGlobalSearchTerm(e.target.value));
            }}
            onFocus={() => {
              setIsFocused(true);
              // If there are existing results, show them
              if (searchResults.length > 0) {
                setIsDropdownOpen(true);
              }
            }}
            onBlur={() => {
              // Delay blur to allow clicking on dropdown items
              setTimeout(() => {
                setIsFocused(false);
              }, 200);
            }}
            onKeyDown={handleKeyDown}
            style={{ paddingRight: searchTerm ? "50px" : "10px" }}
          />
          {searchTerm && (
            <button
              onClick={clearOnClick}
              className="absolute right-7 sm:right-8 md:right-9 top-2.5 text-gray-400 hover:text-gray-600"
              title="Clear search"
            >
              <XIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          )}
          {isLoading && (
            <div className="absolute right-2 sm:right-3 top-3">
              <div className="animate-spin h-3 w-3 sm:h-4 sm:w-4 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
            </div>
          )}
        </div>
      </div>
      {isDropdownOpen && searchResults.length > 0 && (
        <div className="absolute left-0 top-10 w-full md:w-96 max-w-full">
          <ul
            ref={dropdownRef}
            className="bg-white border border-gray-200 pt-2 max-h-96 overflow-y-auto"
          >
            {searchResults.map((result, index) => {
              const config = getResultConfig(result);
              return (
                <li
                  onClick={handleResultClick}
                  className="py-1 px-4 cursor-pointer hover:bg-gray-100"
                  key={index}
                >
                  <Link
                    className="font-medium text-sm no-underline text-black"
                    to={config.link}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-5 w-5 text-gray-600 font-light flex-shrink-0">
                        {config.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{config.title}</span>
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                            {config.label}
                          </span>
                        </div>
                        {config.subtitle && (
                          <div className="text-sm text-gray-500 truncate">
                            {config.subtitle}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
            <li className="mt-2 text-center border-t py-2 px-4 cursor-pointer hover:bg-gray-100">
              <a
                className="no-underline font-medium"
                href={selectedOfficeId
                  ? `/search?term=${encodeURIComponent(searchTerm)}&office_id=${selectedOfficeId}`
                  : `/search?term=${encodeURIComponent(searchTerm)}`}
              >
                Show all results
              </a>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
